import React, { useEffect, useState } from 'react';
import { useI18n } from '@/i18n/I18nContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Info, Building2, Mail, Phone } from 'lucide-react';

const SEEN_KEY = 'switchr-beta-ack-v1';

// A clickable "BETA" badge in the header that opens an info dialog explaining the
// product is in free beta, plus contact details. The dialog auto-opens once per
// browser (localStorage flag) and can be reopened anytime via the badge.
export const BetaInfo = () => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let seen = false;
    try {
      seen = localStorage.getItem(SEEN_KEY) === '1';
    } catch {
      /* ignore */
    }
    if (!seen) setOpen(true);
  }, []);

  const close = () => {
    try {
      localStorage.setItem(SEEN_KEY, '1');
    } catch {
      /* ignore */
    }
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={t('beta_open')}
        aria-label={t('beta_open')}
        data-testid="beta-badge"
        className="inline-flex items-center gap-1 rounded-full border border-[#F0C36D] bg-[#FEF6E3] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#9A6700] transition-colors duration-150 hover:bg-[#FBEFCE] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B7791F]/40"
      >
        {t('beta_badge')}
      </button>

      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
        <DialogContent className="sm:max-w-md" data-testid="beta-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#FEF6E3] text-[#9A6700]">
                <Info className="h-4 w-4" />
              </span>
              {t('beta_title')}
            </DialogTitle>
            <DialogDescription className="pt-1 text-sm leading-relaxed text-foreground/80" data-testid="beta-body">
              {t('beta_body')}
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-lg border border-border bg-muted/40 p-3">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t('beta_contact')}</div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2.5 font-medium text-foreground" data-testid="beta-company">
                <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" /> T2 IT Systeme GmbH
              </div>
              <a href="mailto:info@t2.at" className="flex items-center gap-2.5 text-[#0B5FFF] transition-colors duration-150 hover:underline" data-testid="beta-email">
                <Mail className="h-4 w-4 shrink-0 text-muted-foreground" /> info@t2.at
              </a>
              <a href="tel:+43775522022" className="flex items-center gap-2.5 text-[#0B5FFF] transition-colors duration-150 hover:underline" data-testid="beta-phone">
                <Phone className="h-4 w-4 shrink-0 text-muted-foreground" /> +43 7755 22022
              </a>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={close} className="bg-[#0B5FFF] hover:bg-[#0a55e6]" data-testid="beta-ack-button">
              {t('beta_ack')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
